// Token refresh + socket auth refusal: the pure pieces (no Vue, no socket),
// so they run under `npm test` (node:test).
//
// Why: users migrated from dev hold JWTs signed with the dev key. Prod accepts
// them for a window through a fallback verifier and, on every request it
// verifies that way, answers with a fresh prod-key token in the
// X-CloudLoop-Token-Refresh response header (and, on the socket, a
// `token_refresh` event / the `authenticate` ack). The browser swaps that in
// under the same localStorage key so the window can close without anyone
// being logged out.
//
// "Once": a burst of parallel requests sent with the old token can each come
// back with a freshly minted token. Only the first one that arrives while the
// stored token is still the one the request was sent with is applied; the
// rest are stale and ignored, so the socket re-handshakes once, not N times.

export const REFRESH_HEADER = "X-CloudLoop-Token-Refresh";
export const TOKEN_KEY = "JWT";

function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
}

/**
 * Applies refreshed tokens to `storage` (localStorage-like) under `key`.
 * `onRefresh(token)` runs after a token is stored (e.g. re-handshake the
 * socket). Returns { apply, current }.
 *
 * apply(token, sentWith): stores `token` only if
 *   - it is a non-empty string different from the stored token, and
 *   - a token is stored (a refresh never logs a signed-out browser in), and
 *   - the stored token is still `sentWith`, the token the request/handshake
 *     carried (when given) — otherwise another refresh, a login or a logout
 *     already superseded it.
 * Returns true when the token was stored.
 */
export function createTokenRefresher({
  storage,
  key = TOKEN_KEY,
  onRefresh,
} = {}) {
  function current() {
    return storage ? safeGet(storage, key) : null;
  }
  function apply(token, sentWith) {
    if (!storage || typeof token !== "string" || !token) return false;
    const stored = current();
    if (!stored || stored === token) return false;
    if (sentWith !== undefined && sentWith !== stored) return false;
    try {
      storage.setItem(key, token);
    } catch (e) {
      return false;
    }
    if (onRefresh) {
      try {
        onRefresh(token);
      } catch (e) {
        /* a failing listener must not break the response */
      }
    }
    return true;
  }
  return { apply, current };
}

/** URL string of a fetch() input (string, URL or Request). */
export function requestUrl(input) {
  if (typeof input === "string") return input;
  if (input && typeof input.url === "string") return input.url;
  if (input && typeof input.href === "string") return input.href;
  return input == null ? "" : String(input);
}

/** True when `url` targets the CloudLoop API at `apiBase`. */
export function isApiUrl(url, apiBase) {
  if (!url || !apiBase) return false;
  const base = apiBase.replace(/\/+$/, "");
  return (
    url === base || url.startsWith(`${base}/`) || url.startsWith(`${base}?`)
  );
}

/** The refresh token a response carries, or null. */
export function refreshTokenFrom(response) {
  const headers = response && response.headers;
  if (!headers || typeof headers.get !== "function") return null;
  const value = headers.get(REFRESH_HEADER);
  return value ? value.trim() || null : null;
}

/**
 * Wraps `fetchImpl` so API responses carrying REFRESH_HEADER update the
 * stored token. The response is returned untouched.
 */
export function wrapFetch(fetchImpl, { apiBase, refresher }) {
  return function fetchWithTokenRefresh(input, init) {
    const url = requestUrl(input);
    if (!isApiUrl(url, apiBase)) return fetchImpl(input, init);
    const sentWith = refresher.current();
    return fetchImpl(input, init).then((response) => {
      const token = refreshTokenFrom(response);
      if (token) refresher.apply(token, sentWith);
      return response;
    });
  };
}

/**
 * Socket.io refuses a handshake with connect_error: message 'auth-invalid'
 * (a token was presented and failed: expired, bad signature, unknown user)
 * or 'auth-required' (strict mode, no token), data
 * { code: 'AUTH_REQUIRED', error, reason, message }.
 * Returns 'auth-invalid' | 'auth-required' | null (any other error).
 */
export function authRefusalKind(err) {
  if (!err) return null;
  const data = err.data && typeof err.data === "object" ? err.data : null;
  const candidates = [data && data.error, err.message];
  for (const c of candidates) {
    if (c === "auth-invalid" || c === "auth-required") return c;
  }
  if (data && data.code === "AUTH_REQUIRED") {
    return data.reason === "missing" ? "auth-required" : "auth-invalid";
  }
  return null;
}

/**
 * What to do about a refused handshake.
 *  clearToken: drop the stored token (it will never verify again).
 *  relogin: send the user to the login screen.
 *  reconnect: reconnect anonymously so a public listener keeps live updates
 *    (only after a bad token was dropped; a tokenless refusal would just be
 *    refused again, and socket.io never auto-retries a server refusal).
 */
export function authRefusalAction(kind, { hadToken } = {}) {
  if (kind === "auth-invalid") {
    return { clearToken: true, relogin: true, reconnect: !!hadToken };
  }
  if (kind === "auth-required") {
    return { clearToken: !!hadToken, relogin: true, reconnect: false };
  }
  return { clearToken: false, relogin: false, reconnect: false };
}
